'use client';
import { Node } from '@tiptap/core';
import { type NodeViewProps, NodeViewWrapper, ReactNodeViewRenderer } from '@tiptap/react';
import katex from 'katex';
import 'katex/dist/katex.min.css';
import { Box } from '@mui/material';
import { useEffect, useRef } from 'react';

export default function MathInlineComponent(props: NodeViewProps) {
  const { node } = props;
  const formula = node.attrs.formula;
  const displayRef = useRef<HTMLSpanElement>(null);

  useEffect(() => {
    if (displayRef.current) {
      try {
        katex.render(formula, displayRef.current, {
          displayMode: false,
          throwOnError: false,
          strict: 'ignore',
        });
      } catch (error) {
        console.error('LaTeX rendering failed (math-inline):', {
          formula,
          error: error instanceof Error ? error.message : String(error),
        });

        if (displayRef.current) {
          displayRef.current.innerHTML = `
            <span style="color: #f44336; font-family: monospace; font-size: 0.9em;">
              LaTeX error: ${formula}
            </span>
          `;
        }
      }
    }
  }, [formula]);

  if (!formula) return null;

  return (
    <NodeViewWrapper as="span" className="math-inline" data-type="math-inline">
      <Box
        component="span"
        ref={displayRef}
        sx={(theme) => ({
          display: 'inline-block',
          fontFamily: 'inherit',
          fontSize: 'inherit',
          color: theme.palette.text.primary,
        })}
      />
    </NodeViewWrapper>
  );
}

export const MathInline = Node.create({
  name: 'math_inline',
  group: 'inline',
  inline: true,
  atom: true,
  selectable: true,

  addAttributes() {
    return {
      formula: {
        default: '',
        parseHTML: (element) => element.getAttribute('data-formula') || '',
        renderHTML: (attrs: { formula: string }) => ({
          'data-type': 'math-inline',
          'data-formula': attrs.formula,
        }),
      },
    };
  },

  parseHTML() {
    return [{ tag: 'span[data-type="math-inline"]' }];
  },

  renderHTML({ HTMLAttributes }) {
    return ['span', HTMLAttributes];
  },

  addNodeView() {
    return ReactNodeViewRenderer(MathInlineComponent);
  },
});
